import React, { useRef, useEffect, useState } from "react";
import * as THREE from "three";
import { ArrowRight } from "lucide-react";

// Easing helpers
const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
const clamp = (v, a = 0, b = 1) => Math.min(Math.max(v, a), b);
const remap = (v, a, b) => clamp((v - a) / (b - a));

// -------------- Pure THREE.js Hero scene ---------------
function useHeroScene(mountRef, progressRef, mouseRef) {
    useEffect(() => {
        const mount = mountRef.current;
        if (!mount) return;

        const scene = new THREE.Scene();
        scene.background = new THREE.Color("#FEFEFE");

        const isMobileNow = () => window.innerWidth < 768;
        const getFov = () => (isMobileNow() ? 62 : 42);

        const camera = new THREE.PerspectiveCamera(
            getFov(),
            mount.clientWidth / mount.clientHeight,
            0.1,
            100
        );
        camera.position.set(0, 11, 0.001);
        camera.lookAt(0, 0, 0);

        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.setSize(mount.clientWidth, mount.clientHeight);
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        mount.appendChild(renderer.domElement);

        // Lights
        scene.add(new THREE.AmbientLight(0xffffff, 0.85));
        const dir = new THREE.DirectionalLight(0xffffff, 1.3);
        dir.position.set(8, 12, 6);
        dir.castShadow = true;
        dir.shadow.mapSize.set(1024, 1024);
        scene.add(dir);
        const dir2 = new THREE.DirectionalLight(0xffffff, 0.35);
        dir2.position.set(-6, 6, -4);
        scene.add(dir2);

        // Ground – polygonOffset schiebt den Boden in der Tiefe zurück, damit Bauplan-Linien
        // nicht mit dem Boden z-fighten, wenn die Kamera per Parallax minimal wandert.
        const ground = new THREE.Mesh(
            new THREE.PlaneGeometry(40, 40),
            new THREE.MeshStandardMaterial({
                color: 0xfefefe,
                roughness: 1,
                polygonOffset: true,
                polygonOffsetFactor: 1,
                polygonOffsetUnits: 1,
            })
        );
        ground.rotation.x = -Math.PI / 2;
        ground.receiveShadow = true;
        ground.renderOrder = 0;
        scene.add(ground);

        // Blueprint group – deutlich über dem Boden, plus depthTest aus für Linien via renderOrder
        const blueprint = new THREE.Group();
        blueprint.rotation.x = -Math.PI / 2;
        blueprint.position.y = 0.08;
        blueprint.renderOrder = 1;
        scene.add(blueprint);

        const bpMats = [];
        const addLine = (points, color, opacity) => {
            const geo = new THREE.BufferGeometry().setFromPoints(points);
            const mat = new THREE.LineBasicMaterial({
                color,
                transparent: true,
                opacity,
                depthTest: false,
                depthWrite: false,
            });
            const line = new THREE.Line(geo, mat);
            line.renderOrder = 2;
            blueprint.add(line);
            bpMats.push(mat);
        };
        // Grid
        for (let i = 0; i < 21; i++) {
            const x = -5 + i * 0.5;
            addLine(
                [new THREE.Vector3(x, -3.5, 0), new THREE.Vector3(x, 3.5, 0)],
                0x14100d,
                0.06
            );
        }
        for (let i = 0; i < 15; i++) {
            const y = -3.5 + i * 0.5;
            addLine(
                [new THREE.Vector3(-5, y, 0), new THREE.Vector3(5, y, 0)],
                0x14100d,
                0.06
            );
        }
        // Footprint
        const w = 4.6,
            d = 3.4;
        const footprint = [
            [[-w/2,-d/2],[w/2,-d/2]],
            [[w/2,-d/2],[w/2,d/2]],
            [[w/2,d/2],[-w/2,d/2]],
            [[-w/2,d/2],[-w/2,-d/2]],
            [[-0.4,-d/2],[-0.4,d/2 - 0.6]],
            [[-w/2,0.4],[-0.4,0.4]],
            [[0.9,-d/2],[0.9,-0.2]],
            [[-w/2, d/2 + 0.45],[w/2, d/2 + 0.45]],
            [[w/2 + 0.45, -d/2],[w/2 + 0.45, d/2]],
        ];
        footprint.forEach((s) =>
            addLine(
                [new THREE.Vector3(s[0][0], s[0][1], 0), new THREE.Vector3(s[1][0], s[1][1], 0)],
                0x14100d,
                0.85
            )
        );
        // Red axes
        addLine([new THREE.Vector3(-3.2, 0, 0), new THREE.Vector3(3.2, 0, 0)], 0xe20514, 0.6);
        addLine([new THREE.Vector3(0, -2.3, 0), new THREE.Vector3(0, 2.3, 0)], 0xe20514, 0.6);
        // Red markers
        [[-2.3, -1.7], [2.3, -1.7], [2.3, 1.7], [-2.3, 1.7]].forEach((p) => {
            const m = new THREE.Mesh(
                new THREE.CircleGeometry(0.08, 24),
                new THREE.MeshBasicMaterial({
                    color: 0xe20514,
                    transparent: true,
                    opacity: 0.9,
                    depthTest: false,
                    depthWrite: false,
                })
            );
            m.position.set(p[0], p[1], 0);
            m.renderOrder = 2;
            blueprint.add(m);
            bpMats.push(m.material);
        });

        // Building
        const building = new THREE.Group();
        scene.add(building);

        // Foundation – dezenter schwarzer Rahmen um das Haus, dünner Überstand
        const foundation = new THREE.Mesh(
            new THREE.BoxGeometry(w + 0.06, 0.18, d + 0.06),
            new THREE.MeshStandardMaterial({ color: 0x14100d, roughness: 0.9 })
        );
        foundation.castShadow = true;
        foundation.receiveShadow = true;
        building.add(foundation);

        // Walls
        const walls = new THREE.Group();
        building.add(walls);
        // Walls: Oberseite (Deckfläche, sichtbar in der Plan-Phase als grauer Balken)
        // in einem etwas dunkleren Ton als die weissen Wände.
        const wallSideMat = new THREE.MeshStandardMaterial({ color: 0xfefefe, roughness: 0.85 });
        const wallTopMat = new THREE.MeshStandardMaterial({ color: 0xf5f2ed, roughness: 0.8 });
        // BoxGeometry Materialreihenfolge: +x, -x, +y, -y, +z, -z
        const wallsBox = new THREE.Mesh(
            new THREE.BoxGeometry(w, 2.4, d),
            [wallSideMat, wallSideMat, wallTopMat, wallSideMat, wallSideMat, wallSideMat]
        );
        wallsBox.castShadow = true;
        wallsBox.receiveShadow = true;
        walls.add(wallsBox);

        // Windows on front facade (z = +d/2)
        const winW = 0.65,
            winH = 0.85;
        const cols = 3,
            rows = 2;
        const gapX = (w - cols * winW) / (cols + 1);
        for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
                const x = -w / 2 + gapX + winW / 2 + c * (winW + gapX);
                const y = -1.2 + 0.5 + r * 1.1;
                const wm = new THREE.Mesh(
                    new THREE.PlaneGeometry(winW, winH),
                    new THREE.MeshStandardMaterial({ color: 0x14100d, roughness: 0.3, metalness: 0.4 })
                );
                wm.position.set(x, y, d / 2 + 0.01);
                walls.add(wm);
            }
        }
        // Door (red) – an der SEITE des Hauses (rechte Seitenwand)
        const door = new THREE.Mesh(
            new THREE.PlaneGeometry(0.7, 1.1),
            new THREE.MeshStandardMaterial({ color: 0xe20514, roughness: 0.5 })
        );
        door.rotation.y = Math.PI / 2; // 90° gedreht, damit sie auf der Seitenwand liegt
        door.position.set(w / 2 + 0.011, -1.2 + 0.55, 0.2);
        walls.add(door);

        // Dach – flaches modernes Dach mit leichtem Überstand + kleiner Aufbau
        const roof = new THREE.Group();
        building.add(roof);
        const roofSlabMat = new THREE.MeshStandardMaterial({
            color: 0x14100d,
            roughness: 0.65,
            transparent: true,
            opacity: 0,
        });
        const roofSlab = new THREE.Mesh(
            new THREE.BoxGeometry(w + 0.35, 0.22, d + 0.35),
            roofSlabMat
        );
        roofSlab.castShadow = true;
        roofSlab.receiveShadow = true;
        roof.add(roofSlab);
        // Kleiner Dachaufbau (z.B. Attika/Kamin)
        const roofBoxMat = new THREE.MeshStandardMaterial({
            color: 0x0a0806,
            roughness: 0.7,
            transparent: true,
            opacity: 0,
        });
        const roofBox = new THREE.Mesh(
            new THREE.BoxGeometry(0.55, 0.28, 0.55),
            roofBoxMat
        );
        roofBox.position.set(w / 2 - 0.7, 0.25, -d / 2 + 0.7);
        roofBox.castShadow = true;
        roof.add(roofBox);
        roof.visible = false;

        // Init states
        foundation.scale.y = 0.001;
        walls.scale.y = 0.001;
        roof.scale.y = 1;

        // Camera path (Desktop + Mobile Varianten)
        const camStartDesktop = new THREE.Vector3(0, 11, 0.001);
        const camMidDesktop = new THREE.Vector3(0, 7, 6);
        const camEndDesktop = new THREE.Vector3(6.5, 3.8, 8);
        const lookStartDesktop = new THREE.Vector3(0, 0, 0);
        const lookEndDesktop = new THREE.Vector3(0, 1.4, 0);

        // Mobile: weiter weg + Blick tiefer, damit das Modell in den schmalen Rahmen passt
        const camStartMobile = new THREE.Vector3(0, 15, 0.001);
        const camMidMobile = new THREE.Vector3(0, 10, 9);
        const camEndMobile = new THREE.Vector3(7, 5.5, 12);
        const lookStartMobile = new THREE.Vector3(0, 0, 0);
        const lookEndMobile = new THREE.Vector3(0, 1.0, 0);

        // Resize – auch FOV neu setzen, falls Orientation-Wechsel
        const onResize = () => {
            const w = mount.clientWidth;
            const h = mount.clientHeight;
            camera.aspect = w / h;
            camera.fov = getFov();
            camera.updateProjectionMatrix();
            renderer.setSize(w, h);
        };
        window.addEventListener("resize", onResize);

        let raf;
        let disposed = false;
        const animate = () => {
            if (disposed) return;
            const p = progressRef.current || 0;

            const pFound = remap(p, 0.18, 0.42);
            const pWalls = remap(p, 0.38, 0.68);
            const pRoof = remap(p, 0.82, 0.98);

            const foundationH = 0.18 * easeInOut(pFound);
            const wallsH = 2.4 * easeInOut(pWalls);
            const roofY = foundationH + wallsH;

            // Fundament: schmaler schwarzer Rahmen (0.06) rund um die Wände, gleich bleibend
            const foundScaleY = easeInOut(pFound);
            foundation.visible = true;
            foundation.scale.set(1, Math.max(0.0001, foundScaleY || 0.001), 1);
            foundation.position.y = Math.max(0.0001, foundationH / 2);

            walls.scale.y = Math.max(0.001, easeInOut(pWalls));
            walls.position.y = foundationH + wallsH / 2;

            // Dach: fällt von oben herab und faded ein – erst ganz am Ende sichtbar
            const rp = easeInOut(pRoof);
            roof.visible = pRoof > 0;
            const roofFinalY = roofY + 0.11;
            const roofDropFrom = roofFinalY + 3.5;
            roof.position.y = roofDropFrom + (roofFinalY - roofDropFrom) * rp;
            roofSlabMat.opacity = rp;
            roofBoxMat.opacity = rp;

            const bpOpacity = 1 - remap(p, 0.18, 0.45);
            bpMats.forEach((m) => {
                const base = m.color.getHex() === 0xe20514 ? 0.75 : m.color.getHex() === 0x14100d && m === m ? m.opacity : 0.06;
                m.opacity = m.opacity !== undefined ? Math.min(m.opacity, 1) * bpOpacity : bpOpacity;
            });
            // fix opacity properly (recompute each frame from stored base)
            blueprint.children.forEach((child, idx) => {
                if (child.material) {
                    child.material.opacity = bpOpacity * (child.material.userData?.baseOpacity ?? (child.material.color.getHex() === 0xe20514 ? 0.8 : 0.6));
                }
            });

            // Wähle Kamera-Path je nach Viewport (Mobile/Desktop)
            const mobile = isMobileNow();
            const camStart = mobile ? camStartMobile : camStartDesktop;
            const camMid = mobile ? camMidMobile : camMidDesktop;
            const camEnd = mobile ? camEndMobile : camEndDesktop;
            const lookStart = mobile ? lookStartMobile : lookStartDesktop;
            const lookEnd = mobile ? lookEndMobile : lookEndDesktop;

            // Camera
            let pos;
            if (p < 0.5) {
                const t = easeInOut(p / 0.5);
                pos = camStart.clone().lerp(camMid, t);
            } else {
                const t = easeInOut((p - 0.5) / 0.5);
                pos = camMid.clone().lerp(camEnd, t);
            }
            // Very subtle camera parallax only (kein Rotieren am Bauplan/Gebäude)
            // Gate parallax by scroll progress to avoid camera roll near the top-down (degenerate lookAt) phase
            const parallaxGain = clamp((p - 0.2) / 0.2);
            const mx = (mouseRef.current?.x || 0) * 0.05 * parallaxGain;
            const my = (mouseRef.current?.y || 0) * 0.03 * parallaxGain;
            const target = new THREE.Vector3(pos.x + mx, pos.y + my, pos.z);
            camera.position.lerp(target, 0.025);

            const look = lookStart.clone().lerp(lookEnd, easeInOut(p));
            camera.lookAt(look);

            renderer.render(scene, camera);
            raf = requestAnimationFrame(animate);
        };

        // Set base opacities properly
        blueprint.children.forEach((child) => {
            if (child.material) {
                child.material.userData = { baseOpacity: child.material.opacity };
            }
        });

        animate();

        return () => {
            disposed = true;
            cancelAnimationFrame(raf);
            window.removeEventListener("resize", onResize);
            renderer.dispose();
            if (renderer.domElement.parentNode) {
                renderer.domElement.parentNode.removeChild(renderer.domElement);
            }
            scene.traverse((obj) => {
                if (obj.geometry) obj.geometry.dispose();
                if (obj.material) {
                    if (Array.isArray(obj.material)) obj.material.forEach((m) => m.dispose());
                    else obj.material.dispose();
                }
            });
        };
    }, [mountRef, progressRef, mouseRef]);
}

// -------------- Hero Component ---------------
export default function Hero() {
    const wrapRef = useRef(null);
    const mountRef = useRef(null);
    const mouseRef = useRef({ x: 0, y: 0 });
    const progressRef = useRef(0);
    const [progress, setProgress] = useState(0);

    // Scroll listener
    useEffect(() => {
        const compute = () => {
            const el = wrapRef.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            const total = rect.height - window.innerHeight;
            const scrolled = Math.min(Math.max(-rect.top, 0), total);
            const p = total > 0 ? scrolled / total : 0;
            progressRef.current = p;
            setProgress(p);
        };
        compute();
        window.addEventListener("scroll", compute, { passive: true });
        window.addEventListener("resize", compute);
        return () => {
            window.removeEventListener("scroll", compute);
            window.removeEventListener("resize", compute);
        };
    }, []);

    // Mouse listener
    useEffect(() => {
        const onMove = (e) => {
            const nx = (e.clientX / window.innerWidth) * 2 - 1;
            const ny = (e.clientY / window.innerHeight) * 2 - 1;
            mouseRef.current = { x: nx, y: -ny };
        };
        window.addEventListener("mousemove", onMove);
        return () => window.removeEventListener("mousemove", onMove);
    }, []);

    useHeroScene(mountRef, progressRef, mouseRef);

    // Section height: kürzer auf Mobile, damit Animation schneller durch ist
    const [isMobile, setIsMobile] = useState(
        typeof window !== "undefined" ? window.innerWidth < 768 : false
    );
    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const introOpacity = 1 - clamp((progress - 0.05) / 0.25);
    const outroOpacity = clamp((progress - 0.75) / 0.15);

    return (
        <section
            id="top"
            ref={wrapRef}
            data-testid="hero-section"
            className="relative"
            style={{ height: isMobile ? "240vh" : "320vh" }}
        >
            <div className="sticky top-0 h-screen w-full overflow-hidden bg-[#FEFEFE]">
                <div
                    ref={mountRef}
                    className="hero-canvas"
                    data-testid="hero-canvas"
                />

                {/* Grid overlay */}
                <div
                    className="pointer-events-none absolute inset-0 z-[1] opacity-[0.04]"
                    style={{
                        backgroundImage:
                            "linear-gradient(#14100D 1px, transparent 1px), linear-gradient(90deg, #14100D 1px, transparent 1px)",
                        backgroundSize: "80px 80px",
                    }}
                />

                <div className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-12 flex flex-col justify-between pt-24 pb-16">
                    <div
                        className="flex items-center justify-between text-[11px] tracking-[0.28em] uppercase text-[#14100D]/70"
                        style={{ opacity: introOpacity }}
                    >
                        <span>N 47°10′ · E 8°45′</span>
                        <span>Altendorf · Schweiz</span>
                    </div>

                    <div className="max-w-4xl" style={{ opacity: introOpacity }}>
                        <div className="eyebrow mb-6" data-testid="hero-eyebrow">
                            CLER BAU AG · Altendorf
                        </div>
                        <h1
                            data-testid="hero-headline"
                            className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-light leading-[0.95] tracking-tighter text-[#14100D]"
                            style={{ fontFamily: "Outfit, sans-serif" }}
                        >
                            Vom Plan zur
                            <br />
                            <span className="font-medium">soliden Realität.</span>
                        </h1>
                        <p className="mt-8 max-w-xl text-[15px] sm:text-[17px] leading-relaxed text-[#14100D]/75">
                            Maurerarbeiten, Umbauten und Renovationen mit Präzision,
                            Erfahrung und sauberer Ausführung.
                        </p>

                        <div className="mt-10 flex flex-wrap items-center gap-4">
                            <a
                                href="/kontakt"
                                data-testid="hero-cta-primary"
                                className="btn-primary inline-flex items-center gap-3 px-7 py-4 text-[12px] tracking-[0.24em] uppercase font-semibold"
                                style={{ borderRadius: 2 }}
                            >
                                Projekt anfragen
                                <ArrowRight size={16} />
                            </a>
                            <a
                                href="#leistungen"
                                data-testid="hero-cta-secondary"
                                className="btn-secondary inline-flex items-center gap-3 px-7 py-4 text-[12px] tracking-[0.24em] uppercase font-semibold"
                                style={{ borderRadius: 2 }}
                            >
                                Leistungen ansehen
                            </a>
                        </div>
                    </div>

                    <div
                        className="hidden md:flex items-center justify-between text-[11px] tracking-[0.28em] uppercase text-[#14100D]/60"
                        style={{ opacity: Math.max(introOpacity, outroOpacity * 0.7) }}
                    >
                        <div className="flex items-center gap-5">
                            <PhaseDot active={progress < 0.2} label="Plan" />
                            <PhaseDot active={progress >= 0.2 && progress < 0.45} label="Fundament" />
                            <PhaseDot active={progress >= 0.45 && progress < 0.8} label="Aufbau" />
                            <PhaseDot active={progress >= 0.8} label="Fertig" />
                        </div>
                        <span className="hidden lg:block">Scrollen ↓</span>
                    </div>
                </div>

                <div
                    className="absolute right-6 lg:right-12 bottom-24 z-10 text-right"
                    style={{ opacity: outroOpacity }}
                >
                    <div className="text-[11px] tracking-[0.28em] uppercase text-[#E20514] mb-2">
                        Ausführung
                    </div>
                    <div
                        className="text-2xl lg:text-3xl font-medium text-[#14100D]"
                        style={{ fontFamily: "Outfit, sans-serif" }}
                    >
                        Präzision im Detail.
                    </div>
                </div>
            </div>
        </section>
    );
}

function PhaseDot({ active, label }) {
    return (
        <span className="flex items-center gap-2">
            <span
                className="inline-block h-[6px] w-[6px] transition-colors duration-500"
                style={{ background: active ? "#E20514" : "#14100D33" }}
            />
            <span style={{ color: active ? "#14100D" : undefined }}>{label}</span>
        </span>
    );
}
